import React, { JSX } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import { SortField, SortOrder } from './resumeViewerTypes';

interface ResumeViewerToolbarProps {
  count: number;
  title: string;
  isMobile: boolean;
  isSmall: boolean;
  sortField: SortField;
  sortOrder: SortOrder;
  searchQuery: string;
  onSortSelect: (field: SortField, order: SortOrder) => void;
  onSearchChange: (query: string) => void;
}

export function ResumeViewerToolbar(props: ResumeViewerToolbarProps): JSX.Element {
  return (
    <Box
      sx={{
        p: props.isMobile ? 0.5 : 1,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: props.isMobile ? 'background.paper' : 'inherit',
      }}
    >
      <Typography
        variant={props.isSmall ? 'subtitle2' : props.isMobile ? 'subtitle1' : 'h6'}
        gutterBottom
        sx={{ mb: props.isMobile ? 0.5 : 1, fontWeight: 600 }}
      >
        {props.title} ({props.count})
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexDirection: props.isSmall ? 'column' : 'row',
          alignItems: props.isSmall ? 'stretch' : 'center',
        }}
      >
        <TextField
          size="small"
          placeholder="Search resumes..."
          value={props.searchQuery}
          onChange={e => props.onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: props.searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => props.onSearchChange('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: props.isSmall ? '100%' : 200 }}
        />
        <FormControl size="small" sx={{ minWidth: props.isSmall ? '100%' : 180 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={`${props.sortField}-${props.sortOrder}`}
            label="Sort by"
            onChange={e => {
              const [field, order] = e.target.value.split('-') as [SortField, SortOrder];
              props.onSortSelect(field, order);
            }}
          >
            <MenuItem value="updatedAt-desc">Updated (Newest)</MenuItem>
            <MenuItem value="updatedAt-asc">Updated (Oldest)</MenuItem>
            <MenuItem value="createdAt-desc">Created (Newest)</MenuItem>
            <MenuItem value="createdAt-asc">Created (Oldest)</MenuItem>
            <MenuItem value="candidateId-asc">Candidate (A-Z)</MenuItem>
            <MenuItem value="candidateId-desc">Candidate (Z-A)</MenuItem>
            <MenuItem value="jobId-asc">Job (A-Z)</MenuItem>
            <MenuItem value="jobId-desc">Job (Z-A)</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}
